import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { apiFetch } from "@/lib/api";
import {
  applyTheme, applyMode, applyReducedMotion, applyTypography, applyLayout, applyAtmosphere,
  cacheAppearance, readCachedAppearance, FALLBACK_CONFIG,
} from "@/lib/appearance";

const ThemeContext = createContext(null);

export function ThemeProvider({ children }) {
  const { user } = useAuth();
  const cached = useRef(readCachedAppearance());
  const [config, setConfig] = useState(cached.current.config);
  const [mode, setModeState] = useState(cached.current.mode);
  const [reducedMotion, setReducedMotionState] = useState(cached.current.reducedMotion);
  const [overrides, setOverrides] = useState(cached.current.overrides);
  const [atmosphere, setAtmosphere] = useState(cached.current.atmosphere);
  const [atmospherePersonal, setAtmospherePersonalState] = useState(cached.current.atmospherePersonal || "follow");
  const [motifs, setMotifs] = useState([]);
  const [resolved, setResolved] = useState("light");
  const [themeId, setThemeId] = useState(null);
  const [ready, setReady] = useState(false);

  // apply cached appearance instantly (pre-auth pages themed correctly)
  useEffect(() => {
    applyTheme(config, { overrides });
    setResolved(applyMode(mode));
    applyReducedMotion(reducedMotion);
    if (atmosphere) applyAtmosphere(atmosphere, { reducedMotion });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // re-apply when config/overrides change
  useEffect(() => {
    applyTheme(config, { overrides });
    cacheAppearance({ config, overrides });
  }, [config, overrides]);

  // mode handling + system change listener
  useEffect(() => {
    setResolved(applyMode(mode));
    cacheAppearance({ mode });
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = () => { if (mode === "system") setResolved(applyMode("system")); };
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, [mode]);

  useEffect(() => {
    applyReducedMotion(reducedMotion);
    if (atmosphere) applyAtmosphere(atmosphere, { reducedMotion });
    cacheAppearance({ reducedMotion });
  }, [reducedMotion, atmosphere]);

  // load persisted appearance from Supabase once signed in
  useEffect(() => {
    if (!user) { setReady(true); return; }
    (async () => {
      try {
        const a = await apiFetch("/appearance");
        if (a?.config && Object.keys(a.config).length) setConfig(a.config);
        if (a?.mode) setModeState(a.mode);
        setReducedMotionState(!!a.reduced_motion);
        setOverrides(a.overrides || {});
        setThemeId(a.theme_id || null);
        if (a.typography) applyTypography(a.typography, a.custom_fonts || []);
        if (a.layout) applyLayout(a.layout);
        if (a.atmosphere) { setAtmosphere(a.atmosphere); applyAtmosphere(a.atmosphere, { reducedMotion: !!a.reduced_motion }); }
        const personal = a.atmosphere_personal || "follow";
        setAtmospherePersonalState(personal);
        try { setMotifs(await apiFetch("/motifs")); } catch { /* ignore */ }
        cacheAppearance({
          config: a.config, mode: a.mode,
          reducedMotion: a.reduced_motion, overrides: a.overrides || {},
          atmosphere: a.atmosphere, atmospherePersonal: personal,
        });
      } catch { /* keep cached */ } finally { setReady(true); }
    })();
  }, [user]);

  const persist = useCallback((body) => {
    apiFetch("/appearance", { method: "PUT", body: JSON.stringify(body) }).catch(() => {});
  }, []);

  const setMode = useCallback((m) => { setModeState(m); persist({ mode: m }); }, [persist]);
  const setReducedMotion = useCallback((v) => { setReducedMotionState(v); persist({ reduced_motion: v }); }, [persist]);

  // personal atmosphere: "follow" org/theme, or "off" for focus/accessibility.
  const setAtmospherePersonal = useCallback((v) => {
    const val = v === "off" ? "off" : "follow";
    setAtmospherePersonalState(val);
    cacheAppearance({ atmospherePersonal: val });
    persist({ atmosphere: val });
  }, [persist]);

  // re-pull org atmosphere + motif library after an admin saves (Settings).
  const refreshAtmosphere = useCallback(async () => {
    try {
      const a = await apiFetch("/appearance");
      if (a?.atmosphere) { setAtmosphere(a.atmosphere); applyAtmosphere(a.atmosphere, { reducedMotion }); cacheAppearance({ atmosphere: a.atmosphere }); }
      setMotifs(await apiFetch("/motifs"));
    } catch { /* ignore */ }
  }, [reducedMotion]);

  // select a theme for this user (personal override)
  const selectTheme = useCallback((theme) => {
    if (!theme) return;
    setConfig(theme.config);
    setThemeId(theme.id);
    persist({ theme_id: theme.id });
  }, [persist]);

  // revert user to org default
  const resetToDefault = useCallback(async () => {
    await apiFetch("/appearance", { method: "PUT", body: JSON.stringify({ clear_theme: true, overrides: {} }) });
    setOverrides({});
    const a = await apiFetch("/appearance");
    if (a?.config) setConfig(a.config);
    setThemeId(a?.theme_id || null);
    cacheAppearance({ config: a?.config, overrides: {} });
  }, []);

  // personal token override
  const setOverride = useCallback((key, tripletValue) => {
    setOverrides((o) => {
      const next = { ...o, [key.replace(/^--/, "")]: tripletValue };
      persist({ overrides: next });
      return next;
    });
  }, [persist]);

  // live-preview an arbitrary config without persisting (used by preview mode)
  const previewConfig = useCallback((cfg) => applyTheme(cfg || config, { overrides: {} }), [config]);
  const restoreConfig = useCallback(() => applyTheme(config, { overrides }), [config, overrides]);

  const value = {
    config, mode, resolved, reducedMotion, overrides, themeId, ready,
    atmosphere, atmospherePersonal, motifs, setAtmospherePersonal, refreshAtmosphere,
    setMode, setReducedMotion, selectTheme, resetToDefault, setOverride,
    setConfig, setOverrides, previewConfig, restoreConfig,
    fallbackConfig: FALLBACK_CONFIG,
  };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export const useTheme = () => useContext(ThemeContext);
